// The Requests inbox's logic (RequestsPage.tsx), kept free of React so it can be tested: which
// requests each view holds, how long someone has waited, and how each history event reads.

import { CATEGORIES, STATUS_LABELS, isOverdue, needsReply, waitingSince, type Escalation, type EscalationEvent, type Overdue } from "@ask-hr/core/escalations";

export type View = "open" | "needs" | "overdue" | "mine" | "unowned" | "waiting" | "resolved" | "all";

export const VIEWS: readonly (readonly [View, string])[] = [
  ["open", "All open"],
  ["needs", "Needs reply"],
  ["overdue", "Overdue"],
  ["mine", "Mine"],
  ["unowned", "No owner"],
  ["waiting", "Waiting on employee"],
  ["resolved", "Resolved"],
  ["all", "All requests"],
];

// Waiting on HR, who has waited since when, and what's overdue: the same rules as the reminders.
export { isOverdue, lastShared, needsReply, overdue, waitingSince } from "@ask-hr/core/escalations";

export function inView(r: Escalation, view: View, me: string, now = new Date()): boolean {
  switch (view) {
    case "open":
      return r.status !== "resolved";
    case "needs":
      return needsReply(r);
    case "overdue":
      return isOverdue(r, now);
    case "mine":
      return r.assignee?.oid === me && r.status !== "resolved";
    case "unowned":
      return !r.assignee && r.status !== "resolved";
    case "waiting":
      return r.status === "waiting";
    case "resolved":
      return r.status === "resolved";
    case "all":
      return true;
  }
}

export const lastActivity = (r: Escalation) => r.updatedAt;

// Needs reply: who has waited longest first. All open: the same, then the rest by latest
// activity. Overdue: oldest first. Everything else: latest activity first.
export function sortFor(view: View): (a: Escalation, b: Escalation) => number {
  const longestWait = (a: Escalation, b: Escalation) => waitingSince(a).localeCompare(waitingSince(b));
  const latest = (a: Escalation, b: Escalation) => lastActivity(b).localeCompare(lastActivity(a));
  if (view === "needs") return longestWait;
  if (view === "overdue") return (a, b) => a.createdAt.localeCompare(b.createdAt);
  if (view === "open") return (a, b) => Number(needsReply(b)) - Number(needsReply(a)) || (needsReply(a) ? longestWait(a, b) : latest(a, b));
  return latest;
}

// Search covers the number, who asked, what about, their note and conversation, and HR's notes
// and comments.
export function matches(r: Escalation, query: string): boolean {
  const q = query.trim().toLowerCase().replace(/^#/, "");
  if (!q) return true;
  return [
    r.number ? String(r.number) : "",
    r.requester.name,
    r.requester.email,
    CATEGORIES[r.category],
    r.location,
    r.note,
    ...r.excerpt.map((t) => t.text),
    ...r.history.map((h) => `${h.by} ${h.note ?? ""} ${(h.attachments ?? []).map((a) => a.name).join(" ")}`),
  ]
    .join("\n")
    .toLowerCase()
    .includes(q);
}

// "4m", "3h", "2d".
export function age(iso: string, now: Date): string {
  const m = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h` : `${Math.round(h / 24)}d`;
}

// The request's heading: the start of their note, else its topic.
export function titleOf(r: Escalation): string {
  const note = r.note.trim().split("\n")[0] ?? "";
  if (!note) return CATEGORIES[r.category];
  return note.length > 90 ? `${note.slice(0, 88).replace(/\s+\S*$/, "")}…` : note;
}

// The list row's last line: who said what last (HR notes included, marked).
export function snippetOf(r: Escalation): string {
  const last = r.history.filter((h) => h.note || h.attachments?.length).at(-1);
  if (!last) return r.note ? `${first(r.requester.name)}: ${r.note}` : CATEGORIES[r.category];
  const text = last.note || (last.attachments ?? []).map((a) => a.name).join(", ");
  return `${last.visibility === "employee" ? "" : "Note · "}${first(last.by)}: ${text}`;
}

export const first = (name: string) => name.split(" ")[0] ?? name;
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

// The small grey lines in the thread: status and owner changes. A status that came with a comment
// to the employee or their reply happened by itself, so it doesn't name anyone.
export function eventLines(h: EscalationEvent, index: number): string[] {
  const out: string[] = [];
  if (h.status && !(index === 0 && h.status === "new")) {
    const said = h.visibility === "employee" && Boolean(h.note || h.attachments?.length);
    if (h.status === "resolved") out.push(`${h.by} resolved this request`);
    else if (said) out.push(`Status changed to ${STATUS_LABELS[h.status]}`);
    else out.push(`${h.by} changed the status to ${STATUS_LABELS[h.status]}`);
  }
  if (h.assignee === null) out.push(`${h.by} removed the owner`);
  else if (h.assignee) out.push(h.assignee.name === h.by ? `${h.by} took this request` : `${h.by} gave this request to ${h.assignee.name}`);
  return out;
}

// The row's markers: "No reply 2d" (HR hasn't written yet), "Unanswered 2d" (their reply waits),
// "Open 9d". Days are business days for replies, calendar days for open.
export function overdueChips(o: Overdue): string[] {
  const out: string[] = [];
  if (o.reply) out.push(`${o.reply.firstReply ? "No reply" : "Unanswered"} ${Math.max(1, o.reply.businessDays)}d`);
  if (o.open) out.push(`Open ${o.open.days}d`);
  return out;
}
